import React, { useState } from 'react'
import {
  Box, Typography, Button, IconButton, TextField, MenuItem,
  Chip, Switch, FormControlLabel, Paper, Divider, Tooltip
} from '@material-ui/core'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore, ExpandLess
} from '@material-ui/icons'

const CAPABILITY_TYPES = [
  { value: 'takeoff', label: 'Takeoff Services' },
  { value: 'plant_material', label: 'Plant Material' },
  { value: 'hardscape_material', label: 'Hardscape Material' },
  { value: 'irrigation_supply', label: 'Irrigation Supply' },
  { value: 'aggregate_rock', label: 'Aggregate / Rock' },
  { value: 'equipment_rental', label: 'Equipment Rental' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'other', label: 'Other' }
]

const BID_SCOPES = ['Demo', 'Grading', 'Drainage', 'Irrigation', 'Hardscape', 'Softscape', 'Lighting', 'Water Feature', 'Cleanup']

const DEFAULT_SUBJECT = 'Takeoff Request: {{projectName}}'
const DEFAULT_BODY = 'Hi {{contactName}},\n\nPlease provide a takeoff for the following project:\n\nProject: {{projectName}}\nClient: {{clientName}}\nScopes: {{requestedScopes}}\n\nPlans: {{plansUrl}}\n\nPlease send your takeoff to {{senderEmail}}.\n\nThank you,\n{{senderName}}'

const VendorCapabilitiesEditor = ({ vendor, onSave }) => {
  const [capabilities, setCapabilities] = useState(vendor?.capabilities || [])
  const [expandedIdx, setExpandedIdx] = useState(null)
  const [dirty, setDirty] = useState(false)

  const addCapability = () => {
    setCapabilities(prev => [...prev, {
      type: 'takeoff',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      emailSubjectTemplate: DEFAULT_SUBJECT,
      emailBodyTemplate: DEFAULT_BODY,
      scopes: [],
      isActive: true
    }])
    setExpandedIdx(capabilities.length)
    setDirty(true)
  }

  const removeCapability = (idx) => {
    setCapabilities(prev => prev.filter((_, i) => i !== idx))
    setExpandedIdx(null)
    setDirty(true)
  }

  const updateField = (idx, field, value) => {
    setCapabilities(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c))
    setDirty(true)
  }

  const toggleScope = (idx, scope) => {
    setCapabilities(prev => prev.map((c, i) => {
      if (i !== idx) return c
      const scopes = c.scopes || []
      return {
        ...c,
        scopes: scopes.includes(scope)
          ? scopes.filter(s => s !== scope)
          : [...scopes, scope]
      }
    }))
    setDirty(true)
  }

  const handleSave = () => {
    if (onSave) onSave({ ...vendor, capabilities })
    setDirty(false)
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          Capabilities ({capabilities.length})
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addCapability} style={{ textTransform: 'none' }}>
          Add Capability
        </Button>
      </Box>

      {capabilities.length === 0 && (
        <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
          No capabilities configured. Add one to enable takeoff requests, material pricing, etc.
        </Typography>
      )}

      {capabilities.map((cap, idx) => {
        const isExpanded = expandedIdx === idx
        const typeLabel = CAPABILITY_TYPES.find(t => t.value === cap.type)?.label || cap.type
        return (
          <Paper key={idx} variant="outlined" style={{ marginBottom: 8, padding: isExpanded ? 12 : 8 }}>
            <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => setExpandedIdx(isExpanded ? null : idx)}>
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              <Chip size="small" label={typeLabel} style={{ marginLeft: 8, marginRight: 8, fontSize: 11 }} color={cap.isActive ? 'primary' : 'default'} />
              <Typography variant="body2" style={{ flex: 1 }}>
                {cap.contactEmail || '(no email)'}
              </Typography>
              {cap.isActive === false && (
                <Chip size="small" label="Inactive" style={{ fontSize: 10, height: 18, backgroundColor: '#ef5350', color: '#fff' }} />
              )}
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeCapability(idx) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {isExpanded && (
              <Box mt={2}>
                <Box display="flex" style={{ gap: 12 }} mb={2}>
                  <TextField
                    select size="small" variant="outlined" label="Type"
                    value={cap.type} onChange={(e) => updateField(idx, 'type', e.target.value)}
                    style={{ width: 200 }}
                  >
                    {CAPABILITY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                  <FormControlLabel
                    control={<Switch checked={cap.isActive !== false} onChange={(e) => updateField(idx, 'isActive', e.target.checked)} size="small" />}
                    label="Active"
                  />
                </Box>

                <Box display="flex" style={{ gap: 12 }} mb={2}>
                  <TextField size="small" variant="outlined" label="Contact Name" fullWidth
                    value={cap.contactName || ''} onChange={(e) => updateField(idx, 'contactName', e.target.value)} />
                  <TextField size="small" variant="outlined" label="Contact Email" fullWidth
                    value={cap.contactEmail || ''} onChange={(e) => updateField(idx, 'contactEmail', e.target.value)} />
                  <TextField size="small" variant="outlined" label="Phone" style={{ width: 180 }}
                    value={cap.contactPhone || ''} onChange={(e) => updateField(idx, 'contactPhone', e.target.value)} />
                </Box>

                <Typography variant="caption" style={{ fontWeight: 600 }}>Scopes</Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 4 }} mb={2} mt={0.5}>
                  {BID_SCOPES.map(scope => (
                    <Chip key={scope} label={scope} size="small" clickable
                      color={(cap.scopes || []).includes(scope) ? 'primary' : 'default'}
                      onClick={() => toggleScope(idx, scope)}
                      style={{ fontSize: 11 }}
                    />
                  ))}
                </Box>

                <Divider style={{ marginBottom: 12 }} />

                <Typography variant="caption" style={{ fontWeight: 600 }}>Email Subject Template</Typography>
                <TextField size="small" variant="outlined" fullWidth
                  value={cap.emailSubjectTemplate || ''} onChange={(e) => updateField(idx, 'emailSubjectTemplate', e.target.value)}
                  style={{ marginBottom: 12, marginTop: 4 }}
                  helperText="Use {{projectName}}, {{clientName}}, {{requestedScopes}}"
                />

                <Typography variant="caption" style={{ fontWeight: 600 }}>Email Body Template</Typography>
                <TextField size="small" variant="outlined" fullWidth multiline rows={6}
                  value={cap.emailBodyTemplate || ''} onChange={(e) => updateField(idx, 'emailBodyTemplate', e.target.value)}
                  style={{ marginTop: 4 }}
                  helperText="Use {{projectName}}, {{clientName}}, {{plansUrl}}, {{requestedScopes}}, {{senderName}}, {{senderEmail}}, {{contactName}}"
                />
              </Box>
            )}
          </Paper>
        )
      })}

      {dirty && (
        <Button variant="contained" color="primary" size="small" onClick={handleSave} style={{ marginTop: 8 }}>
          Save Capabilities
        </Button>
      )}
    </Box>
  )
}

export default VendorCapabilitiesEditor
